import { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { AutoAwesome } from 'components/icons';
import { Button } from 'components/ui/button';
import { t } from 'utils/getTranslations';

/**
 * Says the page shows sample data, above the top bar, with the way out. Outside the inert app, so it keeps taking
 * input. Its sentence wraps on a narrow window rather than being cut, so its height is kept on the document
 * as `--tour-banner-h`, which the layout leaves free while the tour runs.
 */
export default function TourBanner({ onExit }: { onExit: () => void }) {
    const bannerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const banner = bannerRef.current;
        if (!banner) return undefined;
        const { style } = document.documentElement;
        const publish = () => style.setProperty('--tour-banner-h', `${banner.offsetHeight}px`);
        publish();
        const resized = new ResizeObserver(publish);
        resized.observe(banner);
        return () => {
            resized.disconnect();
            style.removeProperty('--tour-banner-h');
        };
    }, []);

    return createPortal(
        <div
            ref={bannerRef}
            role="status"
            className="bg-primary-wash border-primary-wash-border text-foreground text-body1 fixed start-0 end-[var(--ai-side-dock-width,0px)] top-0 z-(--z-tour) flex min-h-9 items-center gap-2.5 border-b py-1.5 ps-5 pe-3 sm:start-64"
        >
            <AutoAwesome aria-hidden className="text-primary size-4 flex-none" />
            <span className="min-w-0 flex-1 text-pretty">{t('tour.banner')}</span>
            <Button variant="outline" size="xs" onClick={onExit}>
                {t('tour.exit')}
            </Button>
        </div>,
        document.body
    );
}
